<template>
  <div class="special-price-form">
    <h4 class="form-title">
      <i class="fal fa-tags"></i>
      {{ programId ? $t('specialPrice.edit_title') : $t('specialPrice.add_title') }}
    </h4>

    <div v-if="loadError" class="notification is-warning">
      <i class="fal fa-exclamation-triangle"></i> {{ loadError }}
    </div>

    <div v-if="loading" class="form-loading">{{ $t('specialPrice.loading') }}</div>

    <template v-else>
      <!--
        Locked while saving: the server's per-row error keys (customers.3.tel) point at the
        rows as they were POSTed, and an edit made during a successful save would be lost.
      -->
      <fieldset class="form-fieldset" :disabled="saving">
      <!-- ຂໍ້ມູນທົ່ວໄປ -->
      <div class="columns is-multiline">
        <div class="column is-6">
          <label class="label">
            {{ $t('specialPrice.program_name') }} <span class="text-danger">* {{ fieldErrs.name || '' }}</span>
          </label>
          <input class="input" type="text" maxlength="191" v-model="form.name"
                 :placeholder="$t('specialPrice.program_name_placeholder')"/>
        </div>
        <div class="column is-3">
          <label class="label">{{ $t('specialPrice.reference') }} <span class="text-danger">{{ fieldErrs.reference || '' }}</span></label>
          <input class="input" type="text" maxlength="191" v-model="form.reference" :placeholder="$t('specialPrice.reference_placeholder')"/>
        </div>
        <div class="column is-3">
          <label class="label">{{ $t('specialPrice.status') }}</label>
          <div class="status-control">
            <vs-switch v-model="form.is_active">
              <template #off>{{ $t('specialPrice.off') }}</template>
              <template #on>{{ $t('specialPrice.status_active') }}</template>
            </vs-switch>
          </div>
        </div>
        <div class="column is-12">
          <label class="label">{{ $t('specialPrice.note') }} <span class="text-danger">{{ fieldErrs.note || '' }}</span></label>
          <textarea class="textarea" rows="2" maxlength="2000" v-model="form.note"></textarea>
        </div>
      </div>

      <!-- ເສັ້ນທາງ -->
      <div class="form-section">
        <h5>{{ $t('specialPrice.route_section_title') }}</h5>
        <p class="form-hint">
          {{ $t('specialPrice.route_hint_1') }} <strong>{{ $t('specialPrice.route_hint_or') }}</strong> {{ $t('specialPrice.route_hint_2') }}
        </p>
        <div class="columns">
          <div class="column is-6">
            <label class="label">{{ $t('specialPrice.start_branch') }} <span class="text-danger">{{ branchError('start_branch_ids') }}</span></label>
            <BranchPicker v-model="form.start_branches" multiple :disabled="saving" :placeholder="$t('specialPrice.all_branches_type_to_search')"/>
            <label class="label sub-label">{{ $t('specialPrice.start_province') }} <span class="text-danger">{{ branchError('start_province_ids') }}</span></label>
            <multiselect
                v-model="form.start_provinces"
                :options="provinceOptions"
                multiple
                label="name"
                track-by="id"
                :placeholder="$t('specialPrice.all_provinces')"
                select-label=""
                selected-label=""
                :deselect-label="$t('specialPrice.remove')"
                :close-on-select="false"
                :disabled="saving"
            >
              <template #noResult>{{ $t('specialPrice.no_province_found') }}</template>
            </multiselect>
          </div>
          <div class="column is-6">
            <label class="label">{{ $t('specialPrice.end_branch') }} <span class="text-danger">{{ branchError('end_branch_ids') }}</span></label>
            <BranchPicker v-model="form.end_branches" multiple :disabled="saving" :placeholder="$t('specialPrice.all_branches_type_to_search')"/>
            <label class="label sub-label">{{ $t('specialPrice.end_province') }} <span class="text-danger">{{ branchError('end_province_ids') }}</span></label>
            <multiselect
                v-model="form.end_provinces"
                :options="provinceOptions"
                multiple
                label="name"
                track-by="id"
                :placeholder="$t('specialPrice.all_provinces')"
                select-label=""
                selected-label=""
                :deselect-label="$t('specialPrice.remove')"
                :close-on-select="false"
                :disabled="saving"
            >
              <template #noResult>{{ $t('specialPrice.no_province_found') }}</template>
            </multiselect>
          </div>
        </div>

        <label class="label">{{ $t('specialPrice.route_scope_label') }} <span class="text-danger">{{ fieldErrs.route_scope || '' }}</span></label>
        <div class="route-scope">
          <label class="radio" v-for="scope in routeScopes" :key="scope.value">
            <input type="radio" name="route_scope" :value="scope.value" v-model="form.route_scope"/>
            {{ $t(scope.labelKey) }}
          </label>
        </div>
        <p class="form-hint">
          {{ $t('specialPrice.route_scope_hint') }}
        </p>
      </div>

      <!-- ເບີລູກຄ້າ -->
      <div class="form-section">
        <h5>
          {{ $t('specialPrice.customers_title', { n: filledCustomerCount }) }}
          <span class="text-danger">{{ fieldErrs.customers || '' }}</span>
        </h5>
        <p class="form-hint">{{ $t('specialPrice.customers_hint') }}</p>
        <div class="row-line" v-for="(customer, index) in form.customers" :key="'c' + index">
          <span class="row-no">{{ index + 1 }}.</span>
          <input class="input row-tel" type="text" maxlength="30" v-model="customer.tel" :placeholder="$t('specialPrice.tel')"
                 :class="{ 'is-danger': fieldErrs['customers.' + index + '.tel'] }"/>
          <input class="input row-name" type="text" maxlength="191" v-model="customer.name" :placeholder="$t('specialPrice.name_optional')"/>
          <vs-button icon flat danger @click="removeCustomer(index)"><i class="fal fa-trash-alt"></i></vs-button>
          <span class="text-danger row-error">{{ fieldErrs['customers.' + index + '.tel'] || '' }}</span>
        </div>
        <div class="row-actions">
          <vs-button flat @click="addCustomer()"><i class="fal fa-plus"></i>&nbsp;{{ $t('specialPrice.add_phone') }}</vs-button>
          <vs-button flat @click="showPaste = !showPaste"><i class="fal fa-paste"></i>&nbsp;{{ $t('specialPrice.paste_phones') }}</vs-button>
        </div>
        <div v-if="showPaste" class="paste-box">
          <textarea class="textarea" rows="5" v-model="pasteText" :placeholder="pastePlaceholder"></textarea>
          <vs-button @click="applyPaste()">{{ $t('specialPrice.add_to_list') }}</vs-button>
          <span class="form-hint">{{ pasteResult }}</span>
        </div>
      </div>

      <!-- ປະເພດສິນຄ້າ -->
      <div class="form-section">
        <h5>
          {{ $t('specialPrice.categories_title') }}
          <span class="text-danger">{{ fieldErrs.categories || '' }}</span>
        </h5>
        <div class="row-line" v-for="(row, index) in form.categories" :key="'k' + index">
          <span class="row-no">{{ index + 1 }}.</span>
          <div class="row-category">
            <multiselect
                v-model="row.category"
                :options="categoryOptions"
                label="name"
                track-by="id"
                :placeholder="$t('specialPrice.select_category')"
                select-label=""
                selected-label=""
                deselect-label=""
                :allow-empty="false"
                :disabled="saving"
            >
              <template #option="{ option }">
                <span>{{ option.name }}</span>
                <small class="option-meta">{{ listPriceText(option.price) }}</small>
              </template>
              <template #noResult>{{ $t('specialPrice.no_category_found') }}</template>
            </multiselect>
          </div>
          <input class="input row-price" type="number" min="1" step="1000" v-model="row.price" :placeholder="$t('specialPrice.special_price')"
                 :class="{ 'is-danger': fieldErrs['categories.' + index + '.price'] }"/>
          <vs-button icon flat danger @click="removeCategory(index)"><i class="fal fa-trash-alt"></i></vs-button>
          <span class="row-list-price" v-if="row.category">
            {{ listPriceText(row.category.price) }}
            <span v-if="isNotCheaper(row)" class="text-warning"> · {{ $t('specialPrice.not_cheaper_than_list') }}</span>
          </span>
          <span class="text-danger row-error">
            {{ fieldErrs['categories.' + index + '.parcel_category_id'] || fieldErrs['categories.' + index + '.price'] || '' }}
          </span>
        </div>
        <div class="row-actions">
          <vs-button flat @click="addCategory()"><i class="fal fa-plus"></i>&nbsp;{{ $t('specialPrice.add_category') }}</vs-button>
        </div>
      </div>

      <!-- Logistics -->
      <div class="form-section">
        <h5>{{ $t('specialPrice.logistics_title') }}</h5>
        <p class="form-hint">
          {{ $t('specialPrice.logistics_hint_1') }} <strong>{{ $t('specialPrice.logistics_hint_cheaper') }}</strong> {{ $t('specialPrice.logistics_hint_2') }}
        </p>
        <div class="logistics-rate">
          <input class="input" type="number" min="1" step="100" v-model="form.logistics_price_per_kg"
                 :placeholder="$t('specialPrice.example_2900')" :class="{ 'is-danger': fieldErrs.logistics_price_per_kg }"/>
          <span class="logistics-unit">{{ $t('common.ກີບ') }} / kg</span>
        </div>
        <span class="text-danger">{{ fieldErrs.logistics_price_per_kg || '' }}</span>

        <div class="logistics-rate logistics-floor">
          <span class="logistics-unit">{{ $t('specialPrice.only_weight_above') }}</span>
          <input class="input" type="number" min="0" step="1" v-model="form.logistics_weight_above_kg"
                 :placeholder="$t('specialPrice.all_weights')" :class="{ 'is-danger': fieldErrs.logistics_weight_above_kg }"/>
          <span class="logistics-unit">kg</span>
        </div>
        <span class="text-danger">{{ fieldErrs.logistics_weight_above_kg || '' }}</span>
        <p class="form-hint">
          {{ $t('specialPrice.weight_floor_hint') }}
        </p>
      </div>

      </fieldset>

      <div v-if="generalError" class="notification is-danger">{{ generalError }}</div>

      <div class="form-buttons">
        <vs-button flat @click="$emit('close')" :disabled="saving">{{ $t('specialPrice.cancel') }}</vs-button>
        <vs-button :loading="saving" :disabled="saving || !!loadError" @click="save()">{{ $t('specialPrice.save') }}</vs-button>
      </div>
    </template>
  </div>
</template>

<script>
import BranchPicker from '@/views/Corporate/SpecialPrice/BranchPicker';
import { fieldErrors, formatKip, friendlyMessage, ROUTE_SCOPES } from '@/views/Corporate/SpecialPrice/errorText';

const emptyForm = () => ({
  name: '',
  reference: '',
  note: '',
  is_active: false,
  logistics_price_per_kg: '',
  logistics_weight_above_kg: '',
  route_scope: 'any',
  start_branches: [],
  end_branches: [],
  start_provinces: [],
  end_provinces: [],
  customers: [{ tel: '', name: '' }],
  categories: [{ category: null, price: '' }],
});

/**
 * Add / edit one customer special price program. The save is a FULL replace on the backend
 * (program + every phone + every category price in one transaction), so the form always posts
 * the whole deal — including route_scope, logistics_weight_above_kg and both province lists, which
 * the backend KEEPS when a (stale) page leaves them out. Blank rows are dropped BEFORE posting so the
 * server's per-row error keys (customers.3.tel) point at the rows on screen.
 */
export default {
  name: 'SpecialPriceProgramForm',

  components: { BranchPicker },

  props: {
    /// null = create
    programId: { type: Number, default: null },
    categoryOptions: { type: Array, default: () => [] },
    provinceOptions: { type: Array, default: () => [] },
  },

  data() {
    return {
      form: emptyForm(),
      loading: false,
      loadError: '',
      saving: false,
      fieldErrs: {},
      generalError: '',
      showPaste: false,
      pasteText: '',
      pasteResult: '',
      /// Bound from script: the template compiler keeps "&#10;" in an attribute as literal text.
      pastePlaceholder: this.$t('specialPrice.paste_placeholder'),
      routeScopes: ROUTE_SCOPES,
    };
  },

  computed: {
    filledCustomerCount() {
      return this.form.customers.filter(customer => (customer.tel || '').trim() !== '').length;
    },
  },

  created() {
    if (this.programId) {
      this.load();
    }
  },

  methods: {
    load() {
      this.loading = true;
      this.loadError = '';
      this.$axios.get(`customer-special-prices/${this.programId}`).then(res => {
        const program = res.data && res.data.data;
        if (program) {
          this.fill(program);
        } else {
          this.loadError = this.$t('specialPrice.load_failed');
        }
        this.loading = false;
      }).catch(error => {
        this.loadError = friendlyMessage(error);
        this.loading = false;
      });
    },

    fill(program) {
      this.form = {
        name: program.name || '',
        reference: program.reference || '',
        note: program.note || '',
        is_active: !!program.is_active,
        logistics_price_per_kg: program.logistics_price_per_kg || '',
        logistics_weight_above_kg: program.logistics_weight_above_kg === null || program.logistics_weight_above_kg === undefined
          ? '' : program.logistics_weight_above_kg,
        route_scope: program.route_scope || 'any',
        start_branches: program.start_branches || [],
        end_branches: program.end_branches || [],
        start_provinces: this.provinceValues(program.start_provinces),
        end_provinces: this.provinceValues(program.end_provinces),
        customers: (program.customers || []).map(customer => ({ tel: customer.tel, name: customer.name || '' })),
        categories: (program.categories || []).map(row => ({
          /// Prefer the live option object (so the dropdown shows it selected); a category deleted
          /// since keeps its own name and is refused by the server on save.
          category: this.categoryOptions.find(option => option.id === row.parcel_category_id)
            || { id: row.parcel_category_id, name: this.$t('specialPrice.deleted_category_name', { name: row.name || '#' + row.parcel_category_id }), price: row.list_price },
          price: row.price,
        })),
      };
      if (!this.form.customers.length) {
        this.addCustomer();
      }
      if (!this.form.categories.length) {
        this.addCategory();
      }
    },

    /// Prefer the live option object; otherwise the backend's own {id, name} (the options may still be
    /// loading, or the province was deleted since — the multiselect matches on id either way).
    provinceValues(provinces) {
      return (provinces || []).map(province => this.provinceOptions.find(option => option.id === province.id)
        || { id: province.id, name: province.name || '#' + province.id });
    },

    addCustomer() {
      this.form.customers.push({ tel: '', name: '' });
    },

    removeCustomer(index) {
      this.form.customers.splice(index, 1);
      if (!this.form.customers.length) {
        this.addCustomer();
      }
      this.fieldErrs = {};
    },

    addCategory() {
      this.form.categories.push({ category: null, price: '' });
    },

    removeCategory(index) {
      this.form.categories.splice(index, 1);
      if (!this.form.categories.length) {
        this.addCategory();
      }
      this.fieldErrs = {};
    },

    /**
     * One phone per line, optional leading "1." numbering, optional name after the number
     * ("02095363033 ນິງເຄື່ອງເຢັນ", "0304455611ຈີນ"). Numbers already in the list are skipped.
     */
    applyPaste() {
      const existing = new Set(this.form.customers.map(customer => (customer.tel || '').replace(/\D/g, '')));
      let added = 0;
      let skipped = 0;
      this.pasteText.split(/\r?\n/).forEach(line => {
        const match = line.replace(/^\s*\d{1,3}[.)]\s+/, '').match(/(\+?\d[\d\s-]{4,}\d)\s*(.*)$/);
        if (!match) {
          return;
        }
        const tel = match[1].replace(/[\s-]/g, '');
        if (existing.has(tel.replace(/\D/g, ''))) {
          skipped += 1;
          return;
        }
        existing.add(tel.replace(/\D/g, ''));
        this.form.customers.push({ tel, name: (match[2] || '').trim() });
        added += 1;
      });
      this.form.customers = this.form.customers.filter(customer => !this.isBlankCustomer(customer));
      if (!this.form.customers.length) {
        this.addCustomer();
      }
      this.pasteResult = this.$t('specialPrice.paste_added', { n: added }) + (skipped ? this.$t('specialPrice.paste_skipped', { n: skipped }) : '');
      this.pasteText = '';
    },

    isBlankCustomer(customer) {
      return (customer.tel || '').trim() === '' && (customer.name || '').trim() === '';
    },

    listPriceText(price) {
      return price ? this.$t('specialPrice.list_price', { price: formatKip(price) }) : this.$t('specialPrice.list_price_by_size');
    },

    isNotCheaper(row) {
      return !!(row.category && row.category.price && row.price !== '' && Number(row.price) >= Number(row.category.price));
    },

    branchError(key) {
      const found = Object.keys(this.fieldErrs).find(field => field === key || field.indexOf(key + '.') === 0);
      return found ? this.fieldErrs[found] : '';
    },

    save() {
      /// Drop only COMPLETELY blank rows first so server error indexes match the rows on screen. A
      /// row with a name but no phone is posted and refused by the server — never silently deleted.
      this.form.customers = this.form.customers.filter(customer => !this.isBlankCustomer(customer));
      this.form.categories = this.form.categories.filter(row => row.category || row.price !== '');

      const rate = String(this.form.logistics_price_per_kg || '').trim();
      const floorValue = this.form.logistics_weight_above_kg;
      const floor = floorValue === null || floorValue === undefined ? '' : String(floorValue).trim();
      const payload = {
        name: this.form.name,
        reference: this.form.reference || null,
        note: this.form.note || null,
        is_active: !!this.form.is_active,
        logistics_price_per_kg: rate === '' ? null : Number(rate),
        logistics_weight_above_kg: floor === '' ? null : Number(floor),
        route_scope: this.form.route_scope || 'any',
        start_branch_ids: this.form.start_branches.map(branch => branch.id),
        end_branch_ids: this.form.end_branches.map(branch => branch.id),
        start_province_ids: this.form.start_provinces.map(province => province.id),
        end_province_ids: this.form.end_provinces.map(province => province.id),
        customers: this.form.customers.map(customer => ({
          tel: (customer.tel || '').trim(),
          name: (customer.name || '').trim() || null,
        })),
        categories: this.form.categories.map(row => ({
          parcel_category_id: row.category ? row.category.id : null,
          price: row.price === '' ? null : Number(row.price),
        })),
      };

      this.saving = true;
      this.fieldErrs = {};
      this.generalError = '';
      const request = this.programId
        ? this.$axios.put(`customer-special-prices/${this.programId}`, payload)
        : this.$axios.post('customer-special-prices', payload);

      request.then(res => {
        this.saving = false;
        this.$emit('saved', {
          program: res.data && res.data.data,
          message: res.data && res.data.message,
          cacheStale: !!(res.data && res.data.cache_stale),
        });
      }).catch(error => {
        this.saving = false;
        if (!this.form.customers.length) {
          this.addCustomer();
        }
        if (!this.form.categories.length) {
          this.addCategory();
        }
        this.fieldErrs = fieldErrors(error);
        this.generalError = friendlyMessage(error);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* One height for every control: a Bulma input is 36px, a vue-multiselect 40px. */
$control-height: 40px;

.form-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.special-price-form {
  /* Fluid: vs-dialog caps its own width (800px at a 1200px viewport). */
  width: 100%;
  max-width: 1000px;
  padding: 4px 8px 12px;
  box-sizing: border-box;
  /* vs-dialog is mounted inside #app, which centres its text. */
  text-align: left;

  /* Outside a .field a Bulma input falls back to the system font, so Lao text loses BoonBaan. */
  .input {
    font-family: $font;
    height: $control-height;
  }
}

/* vs-switch is display: flex — left alone it stretches across the whole column. */
.status-control {
  display: flex;
  align-items: center;
  min-height: $control-height;
}

.form-title {
  font-weight: bold;
  color: #195BFF;
  font-size: 18px;
  margin-bottom: 12px;
}

.form-loading {
  padding: 24px;
  text-align: center;
}

.form-section {
  border-top: 1px solid #eee;
  padding-top: 12px;
  margin-top: 8px;

  h5 {
    font-weight: bold;
    margin-bottom: 4px;
  }
}

.form-hint {
  color: #888;
  font-size: 13px;
  margin-bottom: 8px;
}

.row-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.row-no {
  width: 28px;
  text-align: right;
  color: #888;
}

.row-tel {
  flex: 0 0 170px;
  width: 170px;
}

.row-name {
  flex: 1 1 180px;
  width: auto;
}

.row-category {
  flex: 1 1 280px;
  min-width: 220px;

  /* The empty state is 43px by default (3px taller than a chosen value) and in a smaller font. */
  ::v-deep .multiselect__placeholder {
    padding-top: 0;
    margin-bottom: 8px;
    font-size: 16px;
    line-height: 20px;
  }
}

.row-price {
  flex: 0 0 140px;
  width: 140px;
}

.row-list-price {
  color: #888;
  font-size: 12px;
  width: 100%;
  padding-left: 36px;
}

.row-error {
  width: 100%;
  padding-left: 36px;
  font-size: 12px;
}

.row-error:empty {
  display: none;
}

.row-actions {
  display: flex;
  gap: 8px;
  margin: 6px 0 4px 36px;
}

.paste-box {
  margin-left: 36px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 520px;
}

.option-meta {
  margin-left: 6px;
  color: #888;
}

.logistics-rate {
  display: flex;
  align-items: center;
  gap: 8px;
  /* As wide as the control of an is-6 column above. */
  width: calc(50% - 0.75rem);
  min-width: 260px;
  max-width: 100%;
}

.logistics-unit {
  flex: none;
  color: #888;
  white-space: nowrap;
}

.logistics-floor {
  margin-top: 8px;
}

.sub-label {
  margin-top: 8px;
}

.route-scope {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 4px;

  .radio {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
